import React, { useEffect, useRef } from "react";
import { Box, SxProps, Table, TableBody, TableCell, TableHead, TableRow, Theme } from "@mui/material";

/**
 * A column of the inspection graph table. The same shape the MUI X grid columns had (field /
 * headerName / width / flex / renderCell), so the column definitions read as they did.
 */
export interface InspectionColumn<R = any> {
  field: string;
  headerName?: string;
  /** Fixed width in px. */
  width?: number;
  minWidth?: number;
  /** Share of the width left after fixed columns, relative to the other flex columns. */
  flex?: number;
  renderCell?: (params: { row: R; value: any; field: string }) => React.ReactNode;
}

/** A header spanning several adjacent columns, e.g. "Position" over From / To. */
export interface InspectionColumnGroup {
  groupId: string;
  headerName?: string;
  children: { field: string }[];
}

interface InspectionGridProps<R extends { id: string }> {
  rows: R[];
  columns: InspectionColumn<R>[];
  columnGroups?: InspectionColumnGroup[];
  /** The row matching the playback head; highlighted, and followed while `followActive`. */
  activeIndex?: number;
  followActive?: boolean;
  onRowClick?: (row: R) => void;
  sx?: SxProps<Theme>;
}

// Class names other code relies on: PipeDiagram measures `.inspection-grid` to size the pipe and
// finds `.row-<i>` to aim its arrows; the PDF export cuts pages between `.inspection-grid-row`s.
export const INSPECTION_GRID_CLASS = "inspection-grid";
export const INSPECTION_GRID_ROW_CLASS = "inspection-grid-row";

/**
 * The inspection graph's table: a plain MUI Table, not a data grid.
 *
 * Every row is rendered, always. The pipe diagram lines its arrows up with the row elements and
 * the PDF export captures the live DOM, so a virtualised or paginated grid would silently lose
 * rows from both. It also needs no MUI X licence.
 */
export function InspectionGrid<R extends { id: string }>({
  rows,
  columns,
  columnGroups = [],
  activeIndex = -1,
  followActive = false,
  onRowClick,
  sx,
}: InspectionGridProps<R>) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const rowRefs = useRef<(HTMLTableRowElement | null)[]>([]);

  // Keep the active row in view inside this table's own scroll area only; never scroll the page,
  // which would pull the video player away while it plays.
  useEffect(() => {
    if (!followActive || activeIndex < 0) return;
    const root = rootRef.current;
    const row = rowRefs.current[activeIndex];
    if (!root || !row) return;
    const top = row.offsetTop;
    const bottom = top + row.offsetHeight;
    if (top < root.scrollTop) root.scrollTop = top;
    else if (bottom > root.scrollTop + root.clientHeight) root.scrollTop = bottom - root.clientHeight;
  }, [activeIndex, followActive]);

  const totalFlex = columns.reduce((sum, c) => sum + (c.width == null ? c.flex ?? 1 : 0), 0);
  const widthOf = (c: InspectionColumn<R>) =>
    c.width != null ? c.width : `${((c.flex ?? 1) / (totalFlex || 1)) * 100}%`;

  const groupOf = (field: string) =>
    columnGroups.find((g) => g.children.some((child) => child.field === field));
  const hasGroups = columns.some((c) => groupOf(c.field));

  // Tight horizontal padding: MUI's default 16px a side, across eleven columns, cost the
  // table ~350px and pushed it into a sideways scroll beside the pipe and the video.
  const headCellSx = { fontWeight: 600, py: 1, px: 1, lineHeight: 1.3, whiteSpace: "normal" } as const;
  const headCell = (c: InspectionColumn<R>, rowSpan?: number) => (
    <TableCell
      key={c.field}
      rowSpan={rowSpan}
      sx={{ ...headCellSx, width: widthOf(c), minWidth: c.minWidth }}
    >
      {c.headerName ?? ""}
    </TableCell>
  );

  return (
    <Box
      ref={rootRef}
      className={INSPECTION_GRID_CLASS}
      sx={{
        width: "100%",
        // Positioned so a row's offsetTop is measured from here, for the follow-scroll above.
        position: "relative",
        overflow: "auto",
        border: "1px solid rgba(224, 224, 224, 1)",
        borderRadius: 1,
        ...sx,
      }}
    >
      <Table size="small" sx={{ width: "100%" }}>
        <TableHead>
          {hasGroups ? (
            <>
              <TableRow>
                {columns.map((c) => {
                  const group = groupOf(c.field);
                  if (!group) return headCell(c, 2);
                  // The group's label sits over its first child; the other children are spanned.
                  if (group.children[0].field !== c.field) return null;
                  return (
                    <TableCell
                      key={group.groupId}
                      colSpan={group.children.length}
                      align="center"
                      sx={headCellSx}
                    >
                      {group.headerName ?? ""}
                    </TableCell>
                  );
                })}
              </TableRow>
              <TableRow>{columns.filter((c) => groupOf(c.field)).map((c) => headCell(c))}</TableRow>
            </>
          ) : (
            <TableRow>{columns.map((c) => headCell(c))}</TableRow>
          )}
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow
              key={row.id}
              ref={(el: HTMLTableRowElement | null) => {
                rowRefs.current[index] = el;
              }}
              hover
              className={`${INSPECTION_GRID_ROW_CLASS} row-${index}${
                index === activeIndex ? " active-row" : ""
              }`}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              sx={{ height: 64, cursor: onRowClick ? "pointer" : undefined }}
            >
              {columns.map((c) => {
                const value = (row as any)[c.field];
                return (
                  <TableCell
                    key={c.field}
                    sx={{ py: 1, px: 1, lineHeight: "normal", whiteSpace: "normal", wordBreak: "break-word" }}
                  >
                    {c.renderCell ? c.renderCell({ row, value, field: c.field }) : value ?? ""}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

export default InspectionGrid;
